export type SelectionStore = {
  subscribe: (listener: () => void) => () => void;
  has: (id: string) => boolean;
  /** The same set until the selection changes, so a subscriber can compare by identity. */
  getIds: () => ReadonlySet<string>;
  toggle: (id: string) => void;
  /** Replaces the selection. */
  select: (ids: Iterable<string>) => void;
  remove: (ids: Iterable<string>) => void;
  /** Drops every selected id outside `visible`. */
  retain: (visible: ReadonlySet<string>) => void;
};

/**
 * Which attachments are selected, outside React state so a change wakes only the cards and rows
 * whose own selection flipped, not every one the tab renders.
 */
export function createSelectionStore(): SelectionStore {
  let ids: ReadonlySet<string> = new Set();
  const listeners = new Set<() => void>();
  const replace = (next: ReadonlySet<string>) => {
    ids = next;
    for (const listener of listeners) listener();
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    has: (id) => ids.has(id),
    getIds: () => ids,
    toggle: (id) => {
      const next = new Set(ids);
      if (!next.delete(id)) next.add(id);
      replace(next);
    },
    select: (next) => replace(new Set(next)),
    remove: (removing) => {
      const next = new Set(ids);
      for (const id of removing) next.delete(id);
      if (next.size !== ids.size) replace(next);
    },
    retain: (visible) => {
      const next = new Set([...ids].filter((id) => visible.has(id)));
      if (next.size !== ids.size) replace(next);
    },
  };
}
